import type { ReactNode } from 'react';
import React, { Fragment, useEffect, useState, useContext, useCallback } from 'react';

import { EditorPreloadManager } from '@confluence/editor-preloader/entry-points/EditorPreloadManager';
import { useEditSource } from '@confluence/edit-common/entry-points/useEditSource';

import { EditContentButtonPreloaderContext } from './EditContentButtonPreloaderContext';

export type EditContentButtonPreloaderProps = {
	contentId: string;
	spaceKey?: string;
	isEmbeddedEditor?: boolean;
	children: ReactNode;
	editSource: string;
};

export const EditContentButtonPreloader = ({
	contentId,
	spaceKey,
	children,
	editSource,
	isEmbeddedEditor,
}: EditContentButtonPreloaderProps) => {
	const preloader = useContext(EditContentButtonPreloaderContext); // See packages/confluence-fabric-editor/src/editorPreloader.tsx
	const [doPreload, setDoPreload] = useState(false);

	const [{ editSource: contextEditSource }, { setEditSource }] = useEditSource();

	// Cleanup editorPreloadManager on contentId change and unmount.
	useEffect(() => {
		return () => {
			if (doPreload) {
				EditorPreloadManager.removePreloadedId(contentId);
			}
		};
	}, [doPreload, contentId]);

	const onHover = useCallback(async () => {
		if (EditorPreloadManager.shouldPreload(contentId)) {
			setDoPreload(true);
			setEditSource(editSource);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contentId, editSource]);

	return (
		<Fragment>
			{/* eslint-disable-next-line @atlassian/a11y/mouse-events-have-key-events */}
			<div onMouseEnter={onHover}>{children}</div>
			{editSource === contextEditSource && doPreload && preloader
				? preloader({
						contentId,
						spaceKey,
						isEmbeddedEditor,
					})
				: null}
		</Fragment>
	);
};
